/**
 * D24 — is the `.chain` a deep link (`/~/file/<id>`) opens one that still Needs approval?
 *
 * Nobody follows such a chain, so the editor would only load its pointer metadata (or
 * fail). The file DETAIL route doesn't carry the flag; the LISTING row does
 * (`chainNeedsApproval`, the server's one `pointerApprovalState` answer) — so the
 * parent's listing is read (fresh; usually one request, cached for the drive pickers)
 * and the row's own flag decides. Not a chain, or not found in the first page ⇒ false
 * (the viewer opens as it always did; the server still refuses to follow the chain).
 */
import type { QueryClient } from '@tanstack/react-query'
import { isChainPointerRow } from '@papan/types'
import { isSessionEndedError } from '@papan/api-client'
import { fetchAndCacheList, IdentityChangedError } from '@papan/files/client/queries'

/** Asks after the first that an identity change may cost before the check gives up (#1210). */
const IDENTITY_REASKS = 2

export async function chainAwaitsApprovalInListing(
  queryClient: QueryClient,
  info: { id?: string; name: string; fileType?: string | null; parentId?: string | null },
  fileId: string,
): Promise<boolean> {
  if (!isChainPointerRow(info)) return false
  for (let reask = 0; ; reask++) {
    try {
      const list = await fetchAndCacheList(queryClient, info.parentId ?? null, undefined, { fresh: true })
      const row = list.files.find(f => f.id === fileId)
      return !!row?.chainNeedsApproval && !row.chainBroken
    } catch (err) {
      // #1210: refused because the identity changed mid-read — NOT an answer. Ask again as the current
      // identity; after the last ask, throw (the caller doesn't mount the editor): never `false` (a pending
      // chain would open) nor `true` (an approved one would show as pending).
      if (err instanceof IdentityChangedError) {
        // a tenant change: the chain belongs to the other tenant — asking again as this one means nothing
        if (!err.tenantChanged && reask < IDENTITY_REASKS) continue
        throw err
      }
      // #1316 × #1210: an identity switch ENDS the session first, so the read fails as "session ended" — no answer
      // either: thrown (the caller reloads in the new session), never `false` (a pending chain would open as the old
      // identity's) (merge review)
      if (isSessionEndedError(err)) throw err
      return false
    }
  }
}
